/**
 * Geo-blocking constants for preventing expensive Google Maps API calls
 * from scrapers originating from blocked regions.
 *
 * Uses client-side timezone/locale detection (same as fraud detection).
 * This is synchronous and has zero latency impact on normal users.
 */
import { FRAUD_LOCALES, FRAUD_TIMEZONES } from 'constants/fraud';
import { isBot } from 'utils/client/botDetection';
import { GEO_FEATURE_FLAGS } from 'context/GeoLocationContext/config';

/**
 * Debug utility for testing geo-blocking.
 * Call from browser console or import and use in code.
 */
export const debugGeoBlocking = () => {
  if (typeof window === 'undefined') {
    return { error: 'Cannot run on server' };
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || (navigator as any).userLanguage || '';

  const timezoneBlocked = FRAUD_TIMEZONES.includes(timezone);
  const localeBlocked = FRAUD_LOCALES.some((fraudLocale) => locale.startsWith(fraudLocale));

  const result = {
    timezone,
    locale,
    timezoneBlocked,
    localeBlocked,
    isBlocked: timezoneBlocked || localeBlocked,
    blockedTimezones: FRAUD_TIMEZONES,
    blockedLocales: FRAUD_LOCALES,
    googleMaps: {
      loaded: (window as any).__googleMapsLoaded,
      blocked: (window as any).__googleMapsBlocked,
      error: (window as any).__googleMapsError,
      hasGoogle: !!window.google,
      hasMaps: !!window.google?.maps,
    },
  };

  // Only log in non-production
  if (process.env.APP_STAGE !== 'production') {
    console.log('🌍 Geo-Block Debug Info:');
    console.log(`Timezone: ${timezone} ${timezoneBlocked ? '❌ BLOCKED' : '✅ OK'}`);
    console.log(`Locale: ${locale} ${localeBlocked ? '❌ BLOCKED' : '✅ OK'}`);
    console.log(`Overall: ${result.isBlocked ? '🚫 BLOCKED' : '✅ ALLOWED'}`);
    console.log('Google Maps:', result.googleMaps);
  }

  return result;
};

/**
 * Check if user is in a geo-blocked region based on timezone/locale.
 * This is a lenient check - only blocks if we positively detect a blocked region.
 * If timezone/locale is unavailable, we allow through (benefit of the doubt).
 *
 * @returns true if user should be blocked from geo features
 */
export const isGeoBlockedRegion = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    // Check timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone && FRAUD_TIMEZONES.includes(timezone)) {
      return true;
    }

    // Check locale
    const locale = navigator.language || (navigator as any).userLanguage || '';
    if (locale && FRAUD_LOCALES.some((fraudLocale) => locale.startsWith(fraudLocale))) {
      return true;
    }

    return false;
  } catch {
    // If we can't determine, allow through
    return false;
  }
};

/**
 * Check if user is blocked (geo or bot) but in soft mode (can still search).
 * In soft mode, blocked users can use autocomplete and manually search for locations.
 */
export const isInSoftBlockMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (!GEO_FEATURE_FLAGS.ENABLE_SOFT_BLOCK_MODE) return false;

  const geoBlocked = GEO_FEATURE_FLAGS.ENABLE_GEO_BLOCKING && isGeoBlockedRegion();
  const botDetected = GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot();

  return geoBlocked || botDetected;
};

/**
 * Check if user should be completely blocked (hard block - no Google Maps at all).
 * Returns true only when blocking is enabled AND soft mode is disabled.
 */
export const shouldHardBlock = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (GEO_FEATURE_FLAGS.ENABLE_SOFT_BLOCK_MODE) return false; // Soft mode = never hard block

  const geoBlocked = GEO_FEATURE_FLAGS.ENABLE_GEO_BLOCKING && isGeoBlockedRegion();
  const botDetected = GEO_FEATURE_FLAGS.ENABLE_BOT_DETECTION && isBot();

  return geoBlocked || botDetected;
};

/**
 * Where the app looks when it can't or shouldn't use the viewer's own location —
 * bots, geo-blocked visitors, and the map's fallback centre. The market we have
 * inventory in, so the same point `DEFAULT_SEARCH_CENTER` searches from.
 */
export const DEFAULT_FALLBACK_LOCATION = {
  position: {
    latitude: 40.7128,
    longitude: -74.006,
  },
  addressString: 'New York, NY',
};
